/**
 * 우주의 이야기 — 다른 행성들의 최근 활동을 한 줄씩 모은다.
 *
 * 새로 저장하는 것은 없다. 이미 있는 기록에서 만든다.
 *
 * 시간의 정확도가 재료마다 다르다. 이걸 숨기면 거짓말이 된다.
 *   완독·읽기 시작   user_books 는 날짜만 남는다(date). '2시간 전' 을 쓸 수
 *                    없어 '9월 8일' 처럼 날짜로 적는다.
 *   이야기·문장      created_at 이 시:분까지라 '2시간 전' 이 맞다.
 *
 * 내 활동도 함께 넣는다. 처음에는 '다른 행성들' 만 두었는데, 그러면 방금
 * 남긴 내 기록이 이 줄에 보이지 않아 제대로 남았는지 확인할 데가 없었다.
 * 대신 내 카드에는 '같은 책' 을 붙이지 않는다 — 내 책이 내 책과 같다는
 * 말은 뜻이 없다.
 */

const DAYS = 30;

/** "2026.09.08" → "2026-09-08T12:00:00+09:00". 날짜만 있는 것은 한낮에 둔다 —
 *  같은 날의 이야기(시각이 있는 것)와 자연스럽게 섞이게 하기 위해서다.
 *
 *  시간대를 반드시 적는다. 이야기·문장의 created_at 은 UTC(+00:00) 인데
 *  여기에 시간대가 없으면 둘을 견줄 기준이 없다. 예전에는 그 상태로 글자만
 *  비교해서, 한국시간 낮에 쓴 이야기가 같은 날 완독 기록보다 늘 아래로
 *  밀렸다. 날짜는 한국 날짜이므로 +09:00 이 맞다. */
function dayToStamp(display) {
  const d = String(display || "").replaceAll(".", "-");
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00+09:00` : null;
}

/** 며칠 전까지만 본다 */
function within(stamp, now) {
  if (!stamp) return false;
  const t = new Date(stamp).getTime();
  return Number.isFinite(t) && now - t <= DAYS * 86400000 && t <= now + 86400000;
}

/**
 * 언제인지 사람 말로. exact 가 아니면 '몇 시간 전' 을 쓰지 않는다 —
 * 날짜만 아는 것을 시간처럼 적으면 없는 정확도를 지어내게 된다.
 */
export function whenText(stamp, exact, now = Date.now()) {
  const t = new Date(stamp).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = now - t;
  const day = new Date(t);
  const today = new Date(now);
  const sameDay = day.toDateString() === today.toDateString();
  const yesterday = new Date(now - 86400000).toDateString() === day.toDateString();

  if (exact) {
    if (diff < 60000) return "방금";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (sameDay) return `${Math.floor(diff / 3600000)}시간 전`;
  } else if (sameDay) {
    return "오늘";
  }
  if (yesterday) return "어제";
  return `${day.getMonth() + 1}월 ${day.getDate()}일`;
}

/**
 * @param {object} p
 *   allBooks  전체 책(메모 포함)
 *   users     프로필 목록
 *   uid       나
 *   quotes    공개 문장 (listPublicQuotes 가 준 것). 없으면 문장 카드는 없다.
 *   limit     최대 개수
 * @returns {Array} [{ key, type, at, ms, exact, user, book, text, mine, isMe }]
 *   type: done | reading | story | quote
 *   mine: 내 행성에도 있는 책인지 — '같은 책' 표시에 쓴다
 *   isMe: 내 활동인지
 */
export function buildFeed({ allBooks = [], users = [], uid, quotes = [], limit = 80, now = Date.now() } = {}) {
  const byId = new Map((users || []).map((u) => [u.id, u]));
  const mine = new Set();
  for (const b of allBooks) {
    if (b.completedBy?.[uid] || (b.registeredBy?.[uid] && !b.wantToReadBy?.[uid])) mine.add(b.id);
  }

  const out = [];
  const push = (o) => {
    if (!o.user || !within(o.at, now)) return;
    out.push({ ...o, ms: Date.parse(o.at), mine: mine.has(o.book?.id), isMe: o.user.id === uid });
  };

  for (const b of allBooks) {
    for (const [who, day] of Object.entries(b.completedBy || {})) {
      push({ key: `done-${b.id}-${who}`, type: "done", at: dayToStamp(day), exact: false, user: byId.get(who), book: b });
    }
    for (const [who, day] of Object.entries(b.registeredBy || {})) {
      if (b.completedBy?.[who] || b.wantToReadBy?.[who]) continue;
      push({ key: `reading-${b.id}-${who}`, type: "reading", at: dayToStamp(day), exact: false, user: byId.get(who), book: b });
    }
    for (const m of b.memos || []) {
      // 공개한 이야기만. 내 것이라도 혼자 보려고 적은 것은 여기 오지 않는다.
      if (!m.isPublic) continue;
      push({ key: `story-${m.id}`, type: "story", at: m.createdAtRaw, exact: true, user: byId.get(m.userId), book: b, text: m.text, memoId: m.id });
    }
  }

  for (const q of quotes || []) {
    const b = allBooks.find((x) => x.id === q.bookId);
    push({ key: `quote-${q.id}`, type: "quote", at: q.createdAt, exact: true, user: byId.get(q.userId), book: b, text: q.text, quoteId: q.id });
  }

  return out
    // 글자가 아니라 시각으로 센다. 형식이 다른 값(날짜만 / UTC 시각)을
    // 글자로 견주면 실제 순서와 어긋난다.
    .sort((a, b) =>
      b.ms - a.ms ||
      // 같은 날에 담은 책끼리는 시각이 없어 모두 동점이다. 기준이 하나뿐이면
      // 볼 때마다 자리가 바뀌므로, 열쇠로 순서를 고정한다.
      a.key.localeCompare(b.key)
    )
    .slice(0, limit);
}
